import { formatDateRange } from '@/utils';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetCostPerProductiveHourQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import CustomRangePicker from '../../../../../../components/CustomRangePicker';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;

  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0F172A] text-white p-2 rounded border border-[#1f2d40] text-xs">
        <p className="text-center font-bold mb-1">
          {dayjs(label).format('MMM YYYY')}
        </p>
        <p className="text-[#425AA7]">৳{payload[0].value.toFixed(2)}</p>
      </div>
    );
  }

  return null;
};

const CostPerProductiveHours = ({
  department_id,
  office_id,
  filter: allFilters,
  setFilter,
  isFilterChanged,
  resetFilter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
  setFilter: (key: any, value: any) => void;
  isFilterChanged: (key: any) => boolean;
  resetFilter: (key: any) => void;
}) => {
  const filter = allFilters.costPerProductiveHour;

  const { data: costData, error } = useGetCostPerProductiveHourQuery({
    start_date: filter.start_date || '',
    end_date: filter.end_date || '',
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch cost per productive hour breakdown');

  const chartData = (costData?.data?.data?.monthly_breakdown || []).map(
    (item: any) => ({
      name: item.month_date,
      value: item.cost_per_productive_hour,
    }),
  );

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full relative">
      <Flex
        justify="space-between"
        align="center"
        className="mb-4 text-white p-2"
      >
        <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white leading-tight">
          Cost Per Productive Hours
          <span className="text-theme-primary block text-sm">
            {formatDateRange(filter.start_date, filter.end_date)}
          </span>
        </h4>

        <CustomRangePicker
          picker="month"
          startDate={filter.start_date}
          endDate={filter.end_date}
          onChange={(start, end) => {
            const s = dayjs(start).startOf('month').format('YYYY-MM-DD');
            const e = dayjs(end).endOf('month').format('YYYY-MM-DD');
            setFilter('costPerProductiveHour', { start_date: s, end_date: e });
          }}
          disabledDate={(current) =>
            current > dayjs().subtract(1, 'month').endOf('month')
          }
          isChanged={isFilterChanged('costPerProductiveHour')}
          onReset={() => resetFilter('costPerProductiveHour')}
        />
      </Flex>

      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#425AA7" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#425AA7" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#1e2a38"
            vertical={false}
          />
          <XAxis
            dataKey="name"
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(val) => dayjs(val).format('MMM')}
          />
          <YAxis
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => `৳${value}`}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: '#ffffff', strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#0032C0"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorCost)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CostPerProductiveHours;
